// footstep-surface.ts — P2 of plans/ft3d-footsteps-per-step-2026-10-13.md: the surface UNDER THE FOOT.
//
// A surface id (an approved `scanslibrary_*` / `fatetide_*` / `megascans_*` material, a built-in indoor type, a
// room box `interior`, or a terrain word) maps to ONE acoustic class, and a class maps to the footstep pools the
// library already holds (speed_walking/<surface>, 17 pools) with an explicit fallback chain for classes that have
// no takes yet (sand, grass, metal need SoundForge). Pure data + pure functions: no DOM, no catalog access, so the
// websocket bundle's Node tests can import it (feedback_no_dom_at_import_in_websocket_closure).
//
// The P1 encounter-wide rule (`footstepPoolKeys` in roll-sound-route.ts) stays the fallback and keeps its output;
// this module never changes what the roll route plays.
import type { AudioTiers } from './audio-catalog';

export type FootstepSurfaceClass =
  | 'stone' | 'wood' | 'gravel' | 'grass' | 'snow' | 'ice' | 'water' | 'mud' | 'sand' | 'metal' | 'stairs'
  // 2026-10-14: composition classes (bone piles, gore, rugs/bedding, broken glass) — see COMPOSITION_ACOUSTIC_CLASS.
  | 'bone' | 'flesh' | 'soft' | 'glass';

/**
 * Approved surface catalog (ft3d/engine/surface-material-catalog.php, 88 rows on 2026-10-14) + the built-in indoor
 * types of ft3d-bg-manifest.ts + the legacy room `interior` words + terrain words. Labels in the comments are the
 * catalog names. Walls/ceilings/roofs are mapped too: a roof is walkable and a wall material can be a floor override.
 */
export const SURFACE_ACOUSTIC_CLASS: Readonly<Record<string, FootstepSurfaceClass>> = Object.freeze({
  // ── stone / masonry / ceramic / glass (hard, dense, no ring) ──
  scanslibrary_pe9d59: 'stone', // Roman Stone Floor (Old Stone Dungeon default floor)
  scanslibrary_9m48ju: 'stone', // Roman Stone Brick
  scanslibrary_gerq5c: 'stone', // Old Stone Wall
  scanslibrary_hzbjt3: 'stone', // Black Rock (Wet Cave / Mine default floor)
  scanslibrary_mfxw5n: 'stone', // Mossy Rock
  scanslibrary_ccdfgr: 'stone', // Marble
  scanslibrary_heayxv: 'stone', // Concrete
  scanslibrary_uekpjg: 'stone', // Concrete
  scanslibrary_wy2hht: 'stone', // Concrete Tiles
  scanslibrary_efgsem: 'stone', // Asphalt
  scanslibrary_ns8ts3: 'stone', // New Asphalt
  scanslibrary_hvhccm: 'stone', // Old Asphalt
  scanslibrary_ynkdmf: 'stone', // Plaster
  scanslibrary_d67bdm: 'stone', // Plaster Wall
  scanslibrary_mvxfpf: 'stone', // Natural Salt (legacy exterior ground: a crust, not sand)
  grimepit_wall_bricks: 'stone', // Dungeon Wall Bricks
  grimepit_wall_plaster: 'stone', // Dungeon Wall Plaster
  grimepit_rocky_moss_floor: 'stone', // Dungeon Mossy Rock Floor
  fatetide_hex_ceramic_tile_v1: 'stone', // Hex Ceramic Tile
  fatetide_checkered_ceramic_tile_v2: 'stone', // Checkered Ceramic Tile
  fatetide_clear_glass_v1: 'stone', // Clear Glass
  fab_dirtyroof_tiles_01: 'stone', // Dirty Roof Tiles 01 (clay)
  fab_shattered_roof_tiles_1: 'stone', // Shattered Roof Tiles 1
  megascans_wi4obhwg: 'stone', // Dirty Roof Tiles
  megascans_ul0kdffbw: 'stone', // Mossy Roof Tiles
  megascans_ujzlef1n: 'stone', // Mossy Slate Roof Tiles
  megascans_tbjjcgor: 'stone', // Slate Roof
  megascans_td1pcivr: 'stone', // Slate Roof
  megascans_tejwdgos: 'stone', // Slate Roof
  megascans_tfqnfggs: 'stone', // Red Roof Tiles
  megascans_tk4gcgldy: 'stone', // Roof Tiles
  megascans_tlwmdb1ew: 'stone', // Roof Tiles
  megascans_tmdkacdfw: 'stone', // Roof Tiles
  megascans_vd3kcjs: 'stone', // Painted Roof (clay)
  megascans_sf5dn3o: 'stone', // Roof Insulation Tiles
  megascans_sf5x3un: 'stone', // Weathered Roof Insulation Tiles
  // ── wood (planks, shingles, thatch reads as wood decking) ──
  scanslibrary_rqy5vs: 'wood', // Fine Wood
  scanslibrary_8g9yua: 'wood', // Wood Floor
  scanslibrary_5htwcr: 'wood', // Rough Cut Planks
  scanslibrary_wflant: 'wood', // Shingles roof
  megascans_thflebdbw: 'wood', // Slate Wood Old Roof
  megascans_sjelciga: 'wood', // Weathered Wood Slate Roof
  megascans_thtmajkg: 'wood', // Wood Painted Roof Tiles
  megascans_tmllfixn: 'wood', // Wood Roof Shingles
  megascans_tbhvafos: 'grass', // Mossy Thatch Roof (soft, rustling)
  megascans_tksieh3ya: 'grass', // Thatch Roof
  megascans_tlogdeocw: 'grass', // Thatch Roof
  // ── gravel / dirt / soil ──
  grimepit_rocky_dirt: 'gravel', // Dungeon Rocky Dirt
  scanslibrary_nyth43: 'gravel', // Cracked Soil
  scanslibrary_rda4fw: 'gravel', // Red Soil & Gravels
  scanslibrary_aqncnv: 'gravel', // Soil & Stones
  scanslibrary_tandvq: 'gravel', // Grass and Rocks (rocky, not lush)
  scanslibrary_vwakd2: 'gravel', // Grass and Rocks
  // ── grass / leaves / moss ──
  scanslibrary_gccm6u: 'grass', // Forest Ground
  scanslibrary_bcla8t: 'grass', // Grassy Forest Ground
  scanslibrary_qyc4mq: 'grass', // Grass & Lichen Rocks
  scanslibrary_9uma5c: 'grass', // Grass and Soil
  scanslibrary_u6wuku: 'grass', // Moss
  fatetide_marsh_leaf_v1: 'grass', // Marsh Leaf
  fatetide_iris_petals_v1: 'grass', // Iris Petals
  fatetide_cattail_seed_v1: 'grass', // Cattail Seed
  // ── snow / ice ──
  scanslibrary_dzgrza: 'snow', // Rough Snow
  scanslibrary_cq2axg: 'snow', // Snowy Grass
  // ── mud / wet ──
  scanslibrary_yn2us4: 'mud', // Mud
  fatetide_lake_algae_v1: 'mud', // Lake Algae
  // ── sand ──
  scanslibrary_2fuvhh: 'sand', // Sand
  scanslibrary_8uj5gw: 'sand', // Seaweed Sand
  scanslibrary_xdlygz: 'sand', // Wet Sand Pebbles
  // ── metal ──
  scanslibrary_tcfbe5: 'metal', // Rusty Metal
  scanslibrary_bkqgv9_v2: 'metal', // Green Painted Metal
  fatetide_bronze_patina_v1: 'metal', // Worn Bronze with Verdigris
  fatetide_aged_brass_v2: 'metal', // Aged Brass
  fatetide_polished_copper_v2: 'metal', // Polished Copper
  fatetide_blackened_cast_iron_v2: 'metal', // Blackened Cast Iron
  fatetide_white_enamel_iron_v2: 'metal', // White Enamel
  fatetide_institutional_paint_v2: 'metal', // Dark Painted Steel
  fatetide_polished_steel_v1: 'metal', // Polished Steel
  megascans_vdpncdn: 'metal', // Worn Painted Metal Roof
  megascans_vdqkfek: 'metal', // Worn Painted Metal Roof
  megascans_vgdjeetfw: 'metal', // Worn Painted Metal Roof
  // ── textiles / leather / rope: a rug or bedding underfoot is a muffled SOFT step (its own pools since 2026-10-14) ──
  fatetide_brown_leather_v2: 'soft',
  fatetide_leather_strap_trim_v1: 'soft',
  fatetide_linen_v1: 'soft',
  fatetide_wool_upholstery_v1: 'soft',
  fatetide_canvas_v1: 'soft',
  fatetide_burlap_v1: 'soft',
  fatetide_mattress_ticking_v1: 'soft',
  fatetide_hemp_rope_v1: 'soft',
  // ── built-in indoor types (ft3d-bg-manifest.ts INDOOR_TYPE_CATALOG) and legacy room `interior` words ──
  tavern: 'wood', great_hall: 'stone', house: 'wood', shop: 'wood', temple: 'stone', dungeon_room: 'stone',
  cave_chamber: 'gravel', crypt_room: 'stone', ship_interior: 'wood', throne_room: 'stone', library: 'wood',
  prison: 'stone', rock_surface: 'stone', stone: 'stone', wood: 'wood', dirt: 'gravel', cave: 'gravel',
  // ── terrain words (terrain_type rows, weather ground) ──
  water: 'water', shallow_water: 'water', deep_water: 'water', swamp: 'mud', marsh: 'mud', bog: 'mud', mud: 'mud',
  ice: 'ice', frozen: 'ice', snow: 'snow', sand: 'sand', beach: 'sand', desert: 'sand', grass: 'grass',
  leaves: 'grass', gravel: 'gravel', metal: 'metal', stairs: 'stairs',
  // terrain / prop words seen in catalog names ("Dense Brush, Large", "Rubble, Wood, Large", cobbles, boardwalks)
  brush: 'grass', undergrowth: 'grass', thicket: 'grass', moss: 'grass', rubble: 'gravel', debris: 'gravel',
  scree: 'gravel', cobble: 'stone', cobblestone: 'stone', cobbles: 'stone', flagstone: 'stone', flagstones: 'stone',
  boardwalk: 'wood', planks: 'wood', puddle: 'water', puddles: 'water', slush: 'snow', grate: 'metal',
  bone: 'bone', bones: 'bone', skull: 'bone', skulls: 'bone', ossuary: 'bone', gore: 'flesh', flesh: 'flesh',
  carcass: 'flesh', corpse: 'flesh', entrails: 'flesh', rug: 'soft', rugs: 'soft', carpet: 'soft', tapestry: 'soft',
  bedroll: 'soft', bedding: 'soft', hay: 'grass', straw: 'grass', shards: 'glass', glass: 'glass',
});

/**
 * item_composition (the material catalog, `item_type = 'item_composition'`, 32 rows) → class: what a foot standing on an
 * object or a rubble/terrain row made of that material sounds like. Ids are the catalog's (the same ids the roll route's
 * `rules.composition` maps to rubble pools). Gas, energy and magical are null — no surface of their own; the caller
 * falls through to the room / ground underneath.
 */
export const COMPOSITION_ACOUSTIC_CLASS: Readonly<Record<string, FootstepSurfaceClass | null>> = Object.freeze({
  '01D2DFF4-D004-B8A9-6F60-D0261548E1B4': 'metal', // tin
  '19B5896D-0FF0-196F-B46E-B9F77A48F0A5': 'metal', // silver
  '43A405AD-C6FB-B7C5-99B8-1D0720BEF481': 'metal', // gold
  '51FC55E0-49B1-09DD-FF18-216F752292AE': 'metal', // bronze
  '563615C1-273C-476B-9C9A-6672FD33FD5F': 'metal', // lead
  '7140027D-EF3C-6ED2-594E-CF564EDF3466': 'metal', // titanium
  '88389035-9618-33E7-8913-2D21DFA80313': 'metal', // iron
  'BC99B1AB-2B2A-D2B8-5CED-B59DE8B285D1': 'metal', // adamantine
  'C0E90229-3470-BBD1-64BE-2A9875ACACCE': 'metal', // platinum
  'C5F8FC65-A19E-F190-B1AA-8C56F86322FA': 'metal', // steel
  'C81AF954-3771-F865-5F40-F5EEF3650011': 'metal', // brass
  'D1CB5DE2-99EE-1D31-0F67-9541BCDA3702': 'metal', // copper
  'FDDEAE27-8828-BDA3-F4C2-53D1FB9D03F1': 'metal', // mithral
  '2048ACB7-43DD-C3FA-2792-FD32B985250B': 'glass', // glass
  'BDD3F042-222C-F292-4D6B-F6649B99383B': 'wood', // wood
  '7231D794-2C8F-604D-D5D9-FE0DCA0AC4ED': 'wood', // ironwood
  'D1E6CA2B-73EA-4DA6-99AA-45A88203EA6A': 'stone', // stone
  '9DEF31B5-8E21-2372-FE2A-95DB8DF43BFE': 'ice', // ice
  '9D7016EF-6414-6BFC-7E70-9D5C49B242ED': 'bone', // bone
  '7F9BB86D-226E-3EA3-649A-093760FD6EFE': 'bone', // talon
  'F4175703-A404-247E-1049-22D61D8B2C72': 'flesh', // flesh
  '918CE8D2-A017-E894-960D-FBF058E56F40': 'soft', // cloth
  '5AA759C7-EF04-7AA9-8955-0E50FEC7F543': 'soft', // fur
  '8A6D00FB-E8F4-3DDE-AFC9-EB7EE0F108D0': 'soft', // leather
  'DE1072CB-5569-1510-2A91-37DF74F8DE27': 'grass', // fiber (hay, straw, herbs)
  '2F2D12CD-315E-74F7-413E-83C6B73AE77C': 'grass', // paper (a scatter rustles)
  'BFE82356-9958-D674-735F-51A3F2F48AAB': 'grass', // living (plants)
  'D8CB02A5-E748-07DB-E9B7-FE1DD0327DB5': 'water', // liquid
  '5BD53A88-7101-4176-3EE8-1BCA6F284C6E': 'mud', // food (squash underfoot)
  '39D38A22-D343-AFCE-198E-0FEA6907C7BA': null, // gas
  '0EC0D80A-FD30-B083-8BD0-551CE52BD3E3': null, // energy
  '2A4F6284-66F5-8CA4-7740-E6869F9A4391': null, // magical
});

/** The class of a composition id (a UUID, or a layer entry carrying one), or null when unknown / soundless. */
export function footstepCompositionClass(composition: unknown): FootstepSurfaceClass | null {
  const id = typeof composition === 'string' ? composition.trim().toUpperCase() : '';
  return id ? COMPOSITION_ACOUSTIC_CLASS[id] ?? null : null;
}

/**
 * The class for a surface id / word / catalog NAME, or null when it is unknown (the caller falls back to the P1
 * encounter rule). A name is tried whole, then per comma part, then per word — terrain items are named
 * `"<Thing>, <Size>"` ("Thick Mud, Large", "Shallow Water, Medium"), and "Dense Brush" must still read as brush.
 */
export function footstepSurfaceClass(surfaceId: unknown): FootstepSurfaceClass | null {
  if (typeof surfaceId !== 'string') return null;
  const lower = surfaceId.trim().toLowerCase();
  if (!lower) return null;
  const direct = SURFACE_ACOUSTIC_CLASS[lower.replace(/[\s-]+/g, '_')];
  if (direct) return direct;
  if (!/[\s,-]/.test(lower)) return null;
  for (const part of lower.split(',')) {
    const key = part.trim().replace(/[\s-]+/g, '_');
    if (key && SURFACE_ACOUSTIC_CLASS[key]) return SURFACE_ACOUSTIC_CLASS[key]!;
  }
  for (const word of lower.split(/[\s,-]+/)) if (word && SURFACE_ACOUSTIC_CLASS[word]) return SURFACE_ACOUSTIC_CLASS[word]!;
  return null;
}

/**
 * The pools of a class per gait, in preference order: `[walk, run, sneak]` keys under speed_walking. A missing pool is
 * an empty list and resolves through `fallback`. Only pools that EXIST in the library today are named here; add a
 * SoundForge pool by naming it (2026-10-14: SoundForge single-step pools walking/running_grass · walking/running_sand · walking/running_metal were
 * rendered and approved on-site the same day — 43 of 48 takes live).
 */
const CLASS_POOLS: Readonly<Record<FootstepSurfaceClass, { walk: string[]; run: string[]; sneak: string[]; fallback: FootstepSurfaceClass | null }>> = Object.freeze({
  stone: { walk: ['walking_concrete'], run: ['running_hard_floor'], sneak: [], fallback: 'gravel' },
  wood: { walk: ['walking_on_wood'], run: ['running_wood', 'walking_fast_wood'], sneak: [], fallback: 'gravel' },
  gravel: { walk: ['walking_gravel'], run: ['running_gravel'], sneak: ['walking_gravel_slow'], fallback: null },
  grass: { walk: ['walking_grass'], run: ['running_grass'], sneak: ['walking_leaves_sneak'], fallback: 'gravel' },
  snow: { walk: ['walking_snow'], run: ['walking_snow_fast'], sneak: ['walking_carefully_on_ice'], fallback: 'gravel' },
  ice: { walk: ['walking_ice'], run: ['walking_ice'], sneak: ['walking_carefully_on_ice'], fallback: 'snow' },
  water: { walk: ['walking_shallow_water'], run: ['walking_shallow_water'], sneak: ['walking_shallow_water'], fallback: 'mud' },
  mud: { walk: ['walking_swamp'], run: ['walking_swamp'], sneak: ['walking_swamp_half_immersed'], fallback: 'gravel' },
  sand: { walk: ['walking_sand'], run: ['running_sand'], sneak: [], fallback: 'gravel' },
  metal: { walk: ['walking_metal'], run: ['running_metal'], sneak: [], fallback: 'stone' },
  stairs: { walk: ['walking_stairs'], run: ['walking_stairs'], sneak: ['walking_stairs'], fallback: 'stone' },
  // Composition pools rendered 2026-10-14 (SoundForge recipes/foley/speed_walking__*_{bones,flesh,carpet,glass}); until
  // approved their tiers are empty and the chain falls through.
  bone: { walk: ['walking_bones'], run: ['running_bones'], sneak: [], fallback: 'gravel' },
  flesh: { walk: ['walking_flesh'], run: ['running_flesh'], sneak: [], fallback: 'mud' },
  soft: { walk: ['walking_carpet'], run: ['running_carpet'], sneak: [], fallback: 'wood' },
  glass: { walk: ['walking_glass'], run: ['running_glass'], sneak: [], fallback: 'gravel' },
});

/**
 * Ordered pool tiers for a class: the class's own pools for the gait first, then its fallback chain. Every tier is a
 * `speed_walking/<pool>` key list; the picker uses the first tier with entries, so an empty class resolves through the
 * chain without a sidecar. `hidden` movers take the sneak column; a class with no sneak pool sneaks on its walk pool
 * at the player's reduced level, then on the chain's sneak pools.
 */
export function footstepSurfaceTiers(surface: FootstepSurfaceClass, gait: 'walk' | 'run' | 'sprint', hidden = false): AudioTiers {
  const tiers: AudioTiers = [];
  const seen = new Set<FootstepSurfaceClass>();
  const column = hidden ? 'sneak' : gait === 'walk' ? 'walk' : 'run';
  let current: FootstepSurfaceClass | null = surface;
  while (current && !seen.has(current)) {
    seen.add(current);
    const pools: (typeof CLASS_POOLS)[FootstepSurfaceClass] = CLASS_POOLS[current];
    const own = pools[column];
    if (own.length) tiers.push(own.map(pool => `speed_walking/${pool}`));
    else if (hidden && pools.walk.length) tiers.push(pools.walk.map(pool => `speed_walking/${pool}`));
    current = pools.fallback;
  }
  return tiers;
}

/** Every pool key this module can emit — for the inventory probe and the unit test (each must exist in the catalog). */
export function footstepSurfacePoolKeys(): string[] {
  const keys = new Set<string>();
  for (const pools of Object.values(CLASS_POOLS)) for (const list of [pools.walk, pools.run, pools.sneak]) for (const pool of list) keys.add(`speed_walking/${pool}`);
  return [...keys].sort();
}
